/**
 * Per-case task-range requirement overrides (#1314).
 *
 * Attorneys assign document_tasks with custom date ranges (RFPs vary —
 * "5 years of tax documents but only one year of paystubs"). This module
 * turns those assigned ranges into per-category requirement overrides that
 * layer ON TOP of the state-default disclosure windows:
 *
 *   resolution order: attorney task range (widest) → state requirement rows
 *   → state document requirements → category defaults
 *
 * It composes with — and does not replace — the three existing coverage
 * paths (period-based, doc-count, single-doc). A category only gains a
 * task-derived window when the attorney explicitly assigned one.
 */

import {
  DISCLOSURE_CATEGORIES,
  type DisclosureCategory,
} from '@/lib/disclosure/calculateProgress';

// =============================================================================
// Types
// =============================================================================

/** Where a category's effective requirement comes from. */
export type RequirementSource = 'attorney' | 'state';

/** Minimal document_tasks row needed to derive requirement ranges. */
export interface TaskRangeRow {
  id: string;
  document_category_id: string | null;
  account_identifier: string | null;
  date_range_start: string | null; // ISO date (YYYY-MM-DD)
  date_range_end: string | null;   // ISO date (YYYY-MM-DD)
  status: string;
}

/** The widest attorney-assigned range for a category. */
export interface TaskRangeOverride {
  months: number;
  startDate: string;
  endDate: string;
  taskCount: number;
}

export type TaskRangeOverrides = Partial<Record<DisclosureCategory, TaskRangeOverride>>;

/** Category-level date window for gap detection (tasks without an account). */
export interface CategoryDateRange {
  category: DisclosureCategory;
  start: Date;
  end: Date;
}

// =============================================================================
// Category mapping
// =============================================================================

/**
 * Maps document_categories ids (e.g. 'financial.tax_return') — via their
 * suffix after the last '.' — to disclosure categories. Mirrors
 * DOC_TYPE_TO_CATEGORY in useDisclosureCoverage plus template-only aliases.
 */
const TASK_DOC_TYPE_TO_CATEGORY: Record<string, DisclosureCategory> = {
  bank_statement: 'bank_accounts',
  credit_card_statement: 'credit_cards',
  investment_statement: 'investments',
  retirement_statement: 'retirement',
  pay_stub: 'pay_stubs',
  payroll_summary: 'pay_stubs',
  tax_return: 'tax_returns',
  mortgage_statement: 'mortgages',
  property_deed: 'real_estate',
  appraisal: 'real_estate',
  vehicle_title: 'vehicles',
  loan_document: 'promissory_notes',
  insurance_document: 'pc_insurance',
  credit_report: 'credit_report',
  business_pnl: 'business',
  business_valuation: 'business',
  business_financial: 'business',
  schedule_c: 'business',
  form_1120s: 'business',
  corporate_document: 'business',
  lease_agreement: 'leases',
  third_party_payment: 'third_party_payments',
  crypto_statement: 'crypto',
  life_insurance_policy: 'life_insurance',
  health_insurance_card: 'health_insurance',
  social_security_statement: 'ssa_statement',
  w2: 'w2_1099_k1',
  '1099': 'w2_1099_k1',
  k1: 'w2_1099_k1',
  business_tax_return: 'business_tax_returns',
  loan_application: 'loan_applications',
  financial_statement: 'loan_applications',
  income_statement: 'income_statement',
  premarital_agreement: 'premarital_agreements',
  court_order: 'court_support_orders',
  financial_affidavit: 'financial_affidavit',
  // ─── Template-only aliases (AssignDocumentTaskDialog / FL template) ──────
  real_estate: 'real_estate',
  insurance_policy: 'pc_insurance',
};

/**
 * Resolve a document_tasks.document_category_id to a disclosure category.
 * Accepts 'financial.tax_return' style ids, bare document types, and bare
 * disclosure category ids. Returns null when unmappable.
 */
export function mapTaskCategoryToDisclosureCategory(
  categoryId: string | null,
): DisclosureCategory | null {
  if (!categoryId) return null;

  const suffix = categoryId.split('.').pop() ?? categoryId;

  if (suffix in DISCLOSURE_CATEGORIES) {
    return suffix as DisclosureCategory;
  }

  return TASK_DOC_TYPE_TO_CATEGORY[suffix] ?? null;
}

// =============================================================================
// Range math
// =============================================================================

const AVG_DAYS_PER_MONTH = 30.4375; // 365.25 / 12

function parseIsoDate(value: string): Date | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  if (!match) return null;
  const [, y, m, d] = match;
  const date = new Date(Number(y), Number(m) - 1, Number(d));
  return Number.isNaN(date.getTime()) ? null : date;
}

/**
 * Number of months an assigned date range requires.
 * Day-based rounding so both conventions resolve naturally:
 *   2021-06-01 → 2026-06-01 = 60 months (same-day anniversary)
 *   2025-01-01 → 2025-12-31 = 12 months (full calendar year)
 * Sub-month ranges count as 1; inverted/invalid ranges return 0.
 */
export function monthsInRange(start: string, end: string): number {
  const startDate = parseIsoDate(start);
  const endDate = parseIsoDate(end);
  if (!startDate || !endDate) return 0;

  const days = (endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24);
  if (days < 0) return 0;

  return Math.max(1, Math.round(days / AVG_DAYS_PER_MONTH));
}

// =============================================================================
// Override building
// =============================================================================

/**
 * Build per-category requirement overrides from document_tasks rows.
 *
 * Every task with a valid date range and a mappable category contributes,
 * regardless of status — a completed (uploaded/verified) task still defines
 * what the attorney requested for this case. When multiple tasks target the
 * same category, the single WIDEST assigned range wins.
 */
export function buildTaskRangeOverrides(tasks: TaskRangeRow[]): TaskRangeOverrides {
  const overrides: TaskRangeOverrides = {};

  for (const task of tasks) {
    if (!task.date_range_start || !task.date_range_end) continue;

    const category = mapTaskCategoryToDisclosureCategory(task.document_category_id);
    if (!category) continue;

    const months = monthsInRange(task.date_range_start, task.date_range_end);
    if (months <= 0) continue;

    const existing = overrides[category];
    if (!existing) {
      overrides[category] = {
        months,
        startDate: task.date_range_start,
        endDate: task.date_range_end,
        taskCount: 1,
      };
    } else {
      existing.taskCount += 1;
      if (months > existing.months) {
        existing.months = months;
        existing.startDate = task.date_range_start;
        existing.endDate = task.date_range_end;
      }
    }
  }

  return overrides;
}

/**
 * Build category-level date windows for gap detection from tasks that do
 * NOT name a specific account (account-specific tasks already produce
 * per-account custom ranges via buildCustomRangesFromTasks). Widest range
 * per category wins, consistent with buildTaskRangeOverrides.
 */
export function buildCategoryRangesFromTasks(tasks: TaskRangeRow[]): CategoryDateRange[] {
  const widest = new Map<DisclosureCategory, { months: number; start: Date; end: Date }>();

  for (const task of tasks) {
    if (task.account_identifier) continue;
    if (!task.date_range_start || !task.date_range_end) continue;

    const category = mapTaskCategoryToDisclosureCategory(task.document_category_id);
    if (!category) continue;

    const months = monthsInRange(task.date_range_start, task.date_range_end);
    if (months <= 0) continue;

    const start = parseIsoDate(task.date_range_start);
    const end = parseIsoDate(task.date_range_end);
    if (!start || !end) continue;

    const existing = widest.get(category);
    if (!existing || months > existing.months) {
      widest.set(category, { months, start, end });
    }
  }

  return Array.from(widest.entries()).map(([category, range]) => ({
    category,
    start: range.start,
    end: range.end,
  }));
}

// =============================================================================
// Auto-progress
// =============================================================================

/** Statuses eligible for auto-progression to 'uploaded'. */
const AUTO_PROGRESS_STATUSES = new Set(['pending', 'in_progress']);

/** Minimal coverage shape needed to decide auto-progression. */
export interface CoverageForAutoProgress {
  categoryId: string;
  isComplete: boolean;
}

/**
 * Return the ids of range-driven tasks whose category requirement is now
 * fully covered, so their status can auto-progress to 'uploaded'.
 * Only pending / in_progress tasks move; client- or advisor-set states
 * (uploaded, verified, cant_locate, rejected) are never touched.
 */
export function computeTasksToAutoProgress(
  tasks: TaskRangeRow[],
  coverage: CoverageForAutoProgress[],
): string[] {
  const completeCategories = new Set(
    coverage.filter((c) => c.isComplete).map((c) => c.categoryId),
  );

  const ids: string[] = [];
  for (const task of tasks) {
    if (!AUTO_PROGRESS_STATUSES.has(task.status)) continue;
    if (!task.date_range_start || !task.date_range_end) continue;

    const category = mapTaskCategoryToDisclosureCategory(task.document_category_id);
    if (!category) continue;
    if (!completeCategories.has(category)) continue;

    ids.push(task.id);
  }

  return ids;
}
